import { useMemo } from "react";
import { useDashboardData } from "@/shared/contexts/DashboardDataContext";
import styles from "./SummaryCards.module.css";

export default function SummaryCards({ date, viewMode }: { date: Date; viewMode?: string }) {
  const { entries, exportedEntries } = useDashboardData();

  const sameDay = (d1: Date, d2: Date) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  const inRange = (d: Date, from: Date, to: Date) => d >= from && d <= to;

  const stats = useMemo(() => {
    const dayStart = new Date(date); dayStart.setHours(0,0,0,0);
    const dayEnd = new Date(date); dayEnd.setHours(23,59,59,999);
    // Work week: most recent Monday to upcoming Sunday
    const dow = date.getDay(); // 0=Sun,1=Mon,...
    const offsetToMonday = (dow + 6) % 7; // days since Monday
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - offsetToMonday);
    weekStart.setHours(0,0,0,0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23,59,59,999);
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const monthEnd = new Date(date.getFullYear(), date.getMonth()+1, 0, 23,59,59,999);

    const sum = (arr: any[], pred: (d: Date)=>boolean) => arr.reduce((acc, e) => acc + (pred(new Date(e.date)) ? e.duration : 0), 0);

    const dayTracked = sum(entries, (d)=> sameDay(d, date));
    const dayExported = sum(exportedEntries, (d)=> sameDay(d, date));
    const weekTracked = sum(entries, (d)=> inRange(d, weekStart, weekEnd));
    const weekExported = sum(exportedEntries, (d)=> inRange(d, weekStart, weekEnd));
    const monthTracked = sum(entries, (d)=> inRange(d, monthStart, monthEnd));
    const monthExported = sum(exportedEntries, (d)=> inRange(d, monthStart, monthEnd));

    return {
      day: { tracked: dayTracked, exported: dayExported },
      week: { tracked: weekTracked, exported: weekExported, from: weekStart, to: weekEnd },
      month: { tracked: monthTracked, exported: monthExported },
    };
  }, [entries, exportedEntries, date]);

  // Previous design: a single card with three equal blocks (Day / Week / Month)
  // For calendar view, show only the month block
  const renderCards = () => {
    if (viewMode === "calendar") {
      return (
        <div className={`${styles.card} ${styles.cardInline}`}>
          <div className={styles.block}>
            <div className={styles.dateText}>{monthLabel}</div>
            <div className={styles.textRow}>{stats.month.tracked.toFixed(1)} hours to export</div>
            <div className={styles.textRow}>{stats.month.exported.toFixed(1)} hours exported</div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.card}>
        <div className={styles.block}>
          <div className={styles.dateText}>{dayLabel}</div>
          <div className={styles.textRow}>{stats.day.tracked.toFixed(1)} hours to export</div>
          <div className={styles.textRow}>{stats.day.exported.toFixed(1)} hours exported</div>
        </div>
        <div className={styles.block}>
          <div className={styles.dateText}>{monthLabel}</div>
          <div className={styles.textRow}>{stats.month.tracked.toFixed(1)} hours to export</div>
          <div className={styles.textRow}>{stats.month.exported.toFixed(1)} hours exported</div>
        </div>
      </div>
    );
  };

  const dayLabel = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const monthLabel = date.toLocaleDateString(undefined, { month: "short", year: "numeric" });

  return <div className={styles.cardsWrapper}>{renderCards()}</div>;
}
